import { useState, useEffect, useCallback } from 'react';
import { ApiService } from '../services/api';
import { WeeklyLeaderboardResponse } from './useLeaderboard';

interface AvailableWeek {
  weekStartDate: string;
  weekEndDate: string;
  isActive: boolean;
  completedAt?: string;
}

interface WeeklyRewardDistribution {
  id: string;
  weekStartDate: string;
  rewardsConfigId: string;
  gameCountBnbRewards: Array<{ walletAddress: string; bnbAmount?: string; dxfAmount?: string }>;
  gameCountDxfRewards: Array<{ walletAddress: string; bnbAmount?: string; dxfAmount?: string }>;
  dxfMiningBnbRewards: Array<{ walletAddress: string; bnbAmount?: string; dxfAmount?: string }>;
  dxfMiningDxfRewards: Array<{ walletAddress: string; bnbAmount?: string; dxfAmount?: string }>;
  txHashes: {
    gameCountBnb?: string;
    gameCountDxf?: string;
    dxfMiningBnb?: string;
    dxfMiningDxf?: string;
  };
  totalBnbAmount: string;
  totalDxfAmount: string;
  totalRecipients: number;
  createdAt: string;
}

interface HistoricLeaderboardResponse extends WeeklyLeaderboardResponse {
  rewardDistribution?: WeeklyRewardDistribution | null;
}

interface HistoricLeaderboardState {
  availableWeeks: AvailableWeek[];
  currentWeekData: HistoricLeaderboardResponse | null;
  selectedWeekStart: string | null;
  loading: boolean;
  weeksLoading: boolean;
  error: string | null;
}

export const useHistoricLeaderboard = () => {
  const [state, setState] = useState<HistoricLeaderboardState>({
    availableWeeks: [],
    currentWeekData: null,
    selectedWeekStart: null,
    loading: false,
    weeksLoading: true,
    error: null
  });

  // Fetch available weeks
  const fetchAvailableWeeks = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, weeksLoading: true, error: null }));
      
      const data = await ApiService.getAvailableLeaderboardWeeks();
      setState(prev => ({
        ...prev,
        availableWeeks: data.weeks || [],
        weeksLoading: false
      }));

    } catch (error) {
      console.error('Failed to fetch available weeks:', error);
      setState(prev => ({
        ...prev,
        weeksLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch available weeks'
      }));
    }
  }, []);

  // Fetch specific week's leaderboard data
  const fetchWeekData = useCallback(async (weekStartDate: string) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const weekData: HistoricLeaderboardResponse = await ApiService.getHistoricLeaderboard(weekStartDate);
      setState(prev => ({
        ...prev,
        currentWeekData: weekData,
        selectedWeekStart: weekStartDate,
        loading: false
      }));

    } catch (error) {
      console.error(`Failed to fetch week ${weekStartDate} data:`, error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch week data'
      }));
    }
  }, []);

  // Load available weeks on mount
  useEffect(() => {
    fetchAvailableWeeks();
  }, [fetchAvailableWeeks]);

  // Helper function to format wallet address
  const formatWalletAddress = useCallback((address: string): string => {
    if (!address || address.length < 8) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }, []);

  // Helper function to format DXF amount from wei
  const formatDXFAmount = useCallback((weiAmount: string): string => {
    try {
      const amount = parseFloat(weiAmount) / Math.pow(10, 18);
      if (amount >= 1000000) {
        return `${(amount / 1000000).toFixed(2)}M`;
      } else if (amount >= 1000) {
        return `${(amount / 1000).toFixed(2)}K`;
      } else if (amount >= 1) {
        return amount.toFixed(2);
      } else {
        return amount.toFixed(4);
      }
    } catch {
      return '0';
    }
  }, []);

  // Helper function to format BNB amount from wei
  const formatBNBAmount = useCallback((weiAmount: string): string => {
    try {
      const amount = parseFloat(weiAmount) / Math.pow(10, 18);
      return `${amount.toFixed(4)} BNB`;
    } catch {
      return '0 BNB';
    }
  }, []);
  
  // Select specific week
  const selectWeek = useCallback((weekStartDate: string) => {
    if (weekStartDate !== state.selectedWeekStart) {
      fetchWeekData(weekStartDate);
    }
  }, [state.selectedWeekStart, fetchWeekData]);

  // Clear week selection (return to current week)
  const clearWeek = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedWeekStart: null,
      currentWeekData: null
    }));
  }, []);

  // Go to previous week (chronologically)
  const goToPreviousWeek = useCallback(() => {
    if (!state.selectedWeekStart) return;
    
    const currentIndex = state.availableWeeks.findIndex(w => w.weekStartDate === state.selectedWeekStart);
    if (currentIndex > -1 && currentIndex < state.availableWeeks.length - 1) {
      const previousWeek = state.availableWeeks[currentIndex + 1]; // Next in array = previous chronologically
      fetchWeekData(previousWeek.weekStartDate);
    }
  }, [state.selectedWeekStart, state.availableWeeks, fetchWeekData]);

  // Go to next week (chronologically)
  const goToNextWeek = useCallback(() => {
    if (!state.selectedWeekStart) return;
    
    const currentIndex = state.availableWeeks.findIndex(w => w.weekStartDate === state.selectedWeekStart);
    if (currentIndex > 0) {
      const nextWeek = state.availableWeeks[currentIndex - 1]; // Previous in array = next chronologically
      fetchWeekData(nextWeek.weekStartDate);
    }
  }, [state.selectedWeekStart, state.availableWeeks, fetchWeekData]);

  // Check if navigation is available
  const canGoToPrevious = state.selectedWeekStart ? 
    state.availableWeeks.findIndex(w => w.weekStartDate === state.selectedWeekStart) < state.availableWeeks.length - 1 : false;
  
  const canGoToNext = state.selectedWeekStart ? 
    state.availableWeeks.findIndex(w => w.weekStartDate === state.selectedWeekStart) > 0 : false;

  return {
    ...state,
    selectWeek,
    clearWeek,
    goToPreviousWeek,
    goToNextWeek,
    canGoToPrevious,
    canGoToNext,
    refetchWeeks: fetchAvailableWeeks,
    refetchCurrentWeek: state.selectedWeekStart ? () => fetchWeekData(state.selectedWeekStart!) : undefined,
    formatWalletAddress,
    formatDXFAmount,
    formatBNBAmount,
    // Helper to get week display string
    getWeekDisplayString: state.currentWeekData 
      ? `${state.currentWeekData.weekStartDate} - ${state.currentWeekData.weekEndDate}`
      : '',
  };
};

export type { AvailableWeek, WeeklyRewardDistribution, HistoricLeaderboardResponse };